import {
  faAdd,
  faCalendar,
  faCircleExclamation,
  faRotate,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import clsx from "clsx";
import Charts from "../components/Charts";
import StatTiles from "../components/dashboard/stat-tiles";
import DateRangePicker from "../components/date-range-picker";
import { useRangePicker } from "../store/use-range-picker-model";
import UseGraph from "../utils/useGraph";
import ToggleChip from "../components/dashboard/toggle-chip";
import useDashboardData from "../utils/use-dashboard-data";
import DebtSyncAlert from "../components/dashboard/debt-sync-alert";
import BuiltTime from "../components/dashboard/built-time";

export default function Dashboard() {
  const { onToggleRangePicker } = useRangePicker();
  const { setDates, toggleRestartedStats, isRestarted, startDate, endDate } =
    UseGraph();
  const navigate = useNavigate();

  // `true` — this page owns the fetch; the charts below are pure subscribers.
  const {
    data,
    computedAt,
    loading,
    refreshing,
    error,
    showingPrevious,
    isEmpty,
    refresh,
  } = useDashboardData(true);

  const busy = loading || refreshing || showingPrevious;

  const rangeLabel =
    startDate && endDate
      ? `${moment(startDate).format("D MMM")} – ${moment(endDate).format("D MMM YYYY")}`
      : startDate
      ? `From ${moment(startDate).format("D MMM YYYY")}`
      : endDate
      ? `Until ${moment(endDate).format("D MMM YYYY")}`
      : "All time";

  return (
    <div className="bg-background min-h-screen p-4 sm:p-6 lg:p-8">
      {/* ---------- Header ---------- */}
      <header className="flex flex-col sm:flex-row sm:items-center gap-4 animate-fade-in">
        <div className="min-w-0">
          <h1 className="text-[28px] lg:text-[32px] font-bold leading-none bg-gradient-heading bg-clip-text text-transparent">
            Dashboard
          </h1>
          <p className="mt-1.5 text-[13px] font-medium text-light-gray">
            {rangeLabel}
            {isRestarted ? " · restarted chases only" : ""}
          </p>
        </div>

        <button
          onClick={() => navigate("/createChase")}
          className="sm:ml-auto shrink-0 flex gap-2 text-[15px] font-semibold items-center px-5 py-3 text-white bg-gradient-to-r from-accent to-accent-bright rounded-full border border-white/10 shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 transition-all duration-300"
        >
          <FontAwesomeIcon icon={faAdd} />
          New Chase
        </button>
      </header>

      <DebtSyncAlert className="mt-6" />

      {/* ---------- Filter row: one row, above everything it scopes ---------- */}
      <div className="mt-6 flex flex-wrap items-center gap-3 animate-fade-in">
        <DateRangePicker onChange={setDates}>
          <div
            className="h-10 px-4 rounded-full flex items-center gap-2 bg-surface text-text-secondary border border-edge hover:border-edge-strong hover:text-text-primary transition-all duration-300 cursor-pointer text-[13px] font-medium"
            onClick={onToggleRangePicker}
          >
            <FontAwesomeIcon icon={faCalendar} />
            <span className="text-nowrap">{rangeLabel}</span>
          </div>
        </DateRangePicker>

        <ToggleChip
          label="Restarted only"
          checked={isRestarted}
          onChange={toggleRestartedStats}
        />

        <div className="ml-auto flex items-center gap-3">
          {/* Freshness is stated, not implied — the tiles above are live, these are cached. */}
          <span className="text-[12px] font-medium text-light-gray text-nowrap">
            {showingPrevious
              ? "Applying filters…"
              : loading
              ? "Building charts…"
              : refreshing
              ? "Refreshing…"
              : computedAt
              ? <BuiltTime computedAt={computedAt} />
              : ""}
          </span>

          <button
            onClick={() => refresh()}
            disabled={busy}
            title={
              computedAt ? "Rebuild charts now" : "Refresh chart stats"
            }
            className={clsx(
              "h-10 w-10 rounded-full flex items-center justify-center bg-surface text-text-secondary border border-edge transition-all duration-300",
              busy
                ? "cursor-not-allowed opacity-60"
                : "cursor-pointer hover:border-edge-strong hover:text-text-primary"
            )}
          >
            <FontAwesomeIcon icon={faRotate} spin={busy} />
          </button>
        </div>
      </div>

      {/* ---------- Live KPI row ---------- */}
      <div className="mt-5 animate-fade-in-slow">
        <StatTiles />
      </div>

      {/* ---------- Notices ---------- */}
      {error ? (
        <div className="mt-4 px-5 py-4 rounded-2xl border border-amber-500/25 bg-amber-500/5 flex items-center justify-between gap-4 max-sm:flex-wrap">
          <span className="flex items-center gap-2.5 text-[13px] font-medium text-text-secondary">
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className="text-amber-500"
            />
            {error}
          </span>
          <button
            onClick={() => refresh()}
            className="shrink-0 px-4 py-2 rounded-full text-[13px] font-semibold border border-edge bg-surface hover:border-edge-strong hover:text-text-primary transition-all duration-300 cursor-pointer text-nowrap"
          >
            Try again
          </button>
        </div>
      ) : null}

      {isEmpty && !error && !loading ? (
        <div className="mt-4 px-5 py-4 rounded-2xl border border-edge bg-surface text-[13px] font-medium text-light-gray">
          Building chart stats for these filters — the figures above are live in the
          meantime.
        </div>
      ) : null}

      {/* ---------- Charts ----------
          Dimmed rather than blanked while a new filter builds: swapping populated
          charts for skeletons causes a layout jump and reads as a failure. Skeletons
          are only for a genuine first load. */}
      <div
        className={clsx(
          "mt-4 transition-opacity duration-300 animate-fade-in-slow",
          showingPrevious && "opacity-40 pointer-events-none"
        )}
      >
        <Charts stats={data} loading={isEmpty && loading} />
      </div>
    </div>
  );
}
